export type DesktopOs = "mac" | "win" | "linux" | "unknown";

export type DesktopManifest = {
  version: string;
  publishedAt: string | null;
  baseUrl: string;
  downloads: {
    mac: string | null;
    macZip: string | null;
    win: string | null;
    linuxAppImage: string | null;
    linuxDeb: string | null;
  };
};

export type DesktopFormat = {
  id: string;
  label: string;
  hint: string;
  file: string | null;
};

export type DesktopPlatformOffer = {
  id: string;
  os: DesktopOs;
  name: string;
  tagline: string;
  formats: DesktopFormat[];
};

export type DesktopUpdatePhase =
  | "idle"
  | "checking"
  | "available"
  | "downloading"
  | "ready"
  | "error";

export function desktopUpdateStatusHint(
  phase: DesktopUpdatePhase,
  opts: {
    updateMessage: string;
    updateVersion: string | null;
    downloadPct: number | null;
  },
): string {
  const { updateMessage, updateVersion, downloadPct } = opts;
  if (phase === "downloading" && downloadPct != null) return `Downloading… ${downloadPct}%`;
  if (phase === "ready") {
    return `Ready — restart to install${updateVersion ? ` (${updateVersion})` : ""}`;
  }
  if (phase === "available") {
    return `Update available${updateVersion ? `: ${updateVersion}` : ""}`;
  }
  if (phase === "checking") return "Checking…";
  if (phase === "idle" && updateMessage) return updateMessage;
  if (phase === "error" && updateMessage) return updateMessage;
  return "Check for the latest Studio build";
}

export type StudioDesktopBridge = {
  isDesktopShell: true;
  platform: string;
  versions: { app: string; electron: string };
  checkForUpdates: () => Promise<{ ok: boolean }>;
  downloadUpdate: () => Promise<{ ok: boolean }>;
  installUpdate: () => Promise<{ ok: boolean }>;
  onUpdateStatus: (
    listener: (payload: {
      phase: DesktopUpdatePhase;
      message?: string;
      progress?: { percent?: number };
      info?: { version?: string };
    }) => void,
  ) => () => void;
  minimizeWindow?: () => void;
  toggleMaximizeWindow?: () => void;
  closeWindow?: () => void;
  isWindowMaximized?: () => Promise<boolean>;
  onWindowMaximizedChanged?: (listener: (maximized: boolean) => void) => () => void;
};

declare global {
  interface Window {
    studioDesktop?: StudioDesktopBridge;
  }
}

export function isStudioDesktopShell(): boolean {
  return typeof window !== "undefined" && Boolean(window.studioDesktop?.isDesktopShell);
}

export function detectDesktopOs(): DesktopOs {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const platform =
    (typeof window !== "undefined" ? window.studioDesktop?.platform : undefined) ||
    navigator.platform ||
    "";
  if (/Mac|iPhone|iPad/i.test(platform) || /Mac OS X/i.test(ua)) return "mac";
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return "win";
  if (/Linux/i.test(platform) || /Linux/i.test(ua)) return "linux";
  return "unknown";
}

export async function fetchDesktopManifest(): Promise<DesktopManifest | null> {
  try {
    const res = await fetch("/desktop/manifest.json", { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as DesktopManifest;
  } catch {
    return null;
  }
}

export function desktopDownloadHref(manifest: DesktopManifest, file: string | null): string | null {
  if (!file) return null;
  const base = manifest.baseUrl.startsWith("http")
    ? manifest.baseUrl
    : `${typeof window !== "undefined" ? window.location.origin : "https://studio.yatishara.com"}${manifest.baseUrl}`;
  return `${base.replace(/\/?$/, "/")}${file}`;
}

export function platformOffers(manifest: DesktopManifest): DesktopPlatformOffer[] {
  const d = manifest.downloads;
  return [
    {
      id: "mac",
      os: "mac",
      name: "macOS",
      tagline: "Apple Silicon & Intel",
      formats: [
        {
          id: "dmg",
          label: "Download .dmg",
          hint: "Drag to Applications",
          file: d.mac || d.macZip,
        },
      ],
    },
    {
      id: "win",
      os: "win",
      name: "Windows",
      tagline: "Windows 10 & 11",
      formats: [
        {
          id: "exe",
          label: "Download installer",
          hint: "64-bit .exe",
          file: d.win,
        },
      ],
    },
    {
      id: "linux",
      os: "linux",
      name: "Linux",
      tagline: "Debian family & portable",
      formats: [
        {
          id: "deb",
          label: "Download .deb",
          hint: "Ubuntu, Mint, Pop!_OS",
          file: d.linuxDeb,
        },
        {
          id: "appimage",
          label: "Download AppImage",
          hint: "No install step",
          file: d.linuxAppImage,
        },
      ],
    },
  ];
}

export function primaryDownloadForOs(
  manifest: DesktopManifest,
  os: DesktopOs,
): { label: string; file: string | null } {
  const d = manifest.downloads;
  switch (os) {
    case "mac":
      return { label: "macOS (.dmg)", file: d.mac || d.macZip };
    case "win":
      return { label: "Windows (.exe)", file: d.win };
    case "linux":
      return {
        label: "Linux (.deb)",
        file: d.linuxDeb || d.linuxAppImage,
      };
    default:
      return { label: "Download", file: null };
  }
}

export function platformHasDownload(offer: DesktopPlatformOffer): boolean {
  return offer.formats.some((f) => Boolean(f.file));
}
